import React, { useMemo, useState } from 'react';
import { Paperclip, Save, ShieldCheck } from 'lucide-react';
import { useAppContext } from '../../context/AppContext';
import DataTable from '../DataTable';
import Badge from '../common/Badge';
import Button from '../common/Button';
import { t } from '../../i18n';

const statusTone = {
  'Por ingresar': 'neutral',
  'Por solicitar': 'neutral',
  'En elaboracion': 'neutral',
  'En tramite': 'warning',
  'En evaluacion': 'warning',
  Aprobado: 'success',
  Rechazado: 'danger'
};

const DevelopmentPermits = () => {
  const {
    renewablePortfolio,
    renewablePermits,
    updateRenewablePermit
  } = useAppContext();

  const [selectedProjectId, setSelectedProjectId] = useState(renewablePortfolio[0]?.id || '');

  const rows = useMemo(
    () => renewablePermits.filter((permit) => permit.projectId === selectedProjectId),
    [renewablePermits, selectedProjectId]
  );

  const approvedCount = useMemo(() => rows.filter((item) => item.status === 'Aprobado').length, [rows]);
  const pendingCount = rows.length - approvedCount;
  const averageProgress = rows.length > 0
    ? Math.round(rows.reduce((sum, item) => sum + item.progress, 0) / rows.length)
    : 0;

  const selectedProjectLabel = useMemo(() => {
    const project = renewablePortfolio.find((item) => item.id === selectedProjectId);
    return project ? `${project.name} • ${project.region}` : '-';
  }, [renewablePortfolio, selectedProjectId]);

  const columns = [
    {
      key: 'name',
      label: t('Permiso / PAS'),
      sortable: true,
      render: (value, row) => (
        <div>
          <p className="font-semibold text-slate-900">{t(value)}</p>
          <p className="text-slate-500 text-xs mt-0.5">{t(row.description)}</p>
        </div>
      )
    },
    { key: 'type', label: t('Tipo'), sortable: true },
    {
      key: 'status',
      label: t('Estado'),
      sortable: true,
      render: (value) => <Badge tone={statusTone[value] || 'neutral'}>{t(value)}</Badge>
    },
    { key: 'requestDate', label: t('Fecha solicitud'), sortable: true, align: 'right', render: (value) => value || '—' },
    { key: 'expectedDate', label: t('Fecha esperada'), sortable: true, align: 'right', render: (value) => value || '—' },
    {
      key: 'progress',
      label: t('Avance'),
      sortable: true,
      render: (value) => (
        <div className="flex items-center gap-2 min-w-[130px]">
          <div className="h-1.5 flex-1 rounded-sm bg-neutral-200 overflow-hidden">
            <div className="h-full bg-brand-600" style={{ width: `${value}%` }} />
          </div>
          <span className="font-semibold text-xs text-slate-700 num">{t(value)}%</span>
        </div>
      )
    },
    { key: 'responsible', label: t('Responsable'), sortable: true },
    {
      key: 'actions',
      label: t('Acciones'),
      render: (value, permit) => (
        <div className="space-y-2 max-w-[260px]">
          <div className="flex items-center gap-2">
            <select
              value={permit.status}
              onChange={(event) => updateRenewablePermit(permit.id, { status: event.target.value })}
              className="flex-1 px-2 py-1.5 rounded border border-slate-200 text-xs"
            >
              <option>{t('Por ingresar')}</option>
              <option>{t('Por solicitar')}</option>
              <option>{t('En elaboracion')}</option>
              <option>{t('En tramite')}</option>
              <option>{t('En evaluacion')}</option>
              <option>{t('Aprobado')}</option>
              <option>{t('Rechazado')}</option>
            </select>
            <Button
              size="sm"
              variant="primary"
              icon={Save}
              onClick={() => updateRenewablePermit(permit.id, { status: permit.status })}
            >{t('Guardar')}</Button>
          </div>
          <div className="rounded-xl border border-slate-200 p-3 bg-slate-50">
            <div className="flex items-center justify-between text-slate-600 text-xs font-semibold">
              <span>{t('Adjuntos')}</span>
              <button type="button" className="text-brand-700 hover:underline">{t('Adjuntar archivo')}</button>
            </div>
            <div className="mt-1 text-xs text-slate-500">
              {permit.attachments?.length > 0 ? (
                permit.attachments.map((attachment) => (
                  <p key={attachment.id} className="mb-1">
                    <span className="inline-flex items-center gap-1"><Paperclip className="w-3.5 h-3.5" /> {t(attachment.name)}</span>
                    <br />
                    {t(attachment.meta)}
                  </p>
                ))
              ) : (
                <p>{t('Sin archivos adjuntos.')}</p>
              )}
            </div>
          </div>
        </div>
      )
    }
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">{t('Permisos y PAS sectoriales')}</h1>
          <p className="text-slate-600 text-sm">{t('Controla RCA, PAS y permisos criticos para avanzar a Ready to Build.')}</p>
          <p className="text-xs text-slate-400 mt-1">{t('Perfil actual: super_user')}</p>
        </div>
        <div className="inline-flex items-center gap-2 text-slate-600 text-sm">
          <ShieldCheck className="w-4 h-4 text-neutral-400" />
          {t(approvedCount)}/{t(rows.length)} {t('aprobados •')} {t(pendingCount)}{t('pendientes')}</div>
      </div>

      <div className="flex items-center gap-3">
        <label className="font-semibold text-slate-600 text-sm">{t('Proyecto')}</label>
        <select
          value={selectedProjectId}
          onChange={(event) => setSelectedProjectId(event.target.value)}
          className="px-3 py-2 rounded-lg border border-slate-200 text-sm"
        >
          {renewablePortfolio.map((project) => (
            <option key={project.id} value={project.id}>{t(project.name)} • {t(project.region)}</option>
          ))}
        </select>
        <span className="text-slate-500 text-sm">{t(selectedProjectLabel)}</span>
        <span className="text-slate-500 text-sm">{t('Avance promedio')} {t(averageProgress)}%</span>
      </div>

      <DataTable
        data={rows}
        columns={columns}
        searchPlaceholder={t('Buscar permiso...')}
        emptyTitle={t('Sin permisos')}
        emptyDescription={t('Este proyecto aun no tiene permisos o PAS registrados.')}
      />

      <div className="rounded-xl bg-info-50 border border-info-100 px-4 py-3 text-info-700 text-sm">{t(
        'Manten actualizadas las observaciones para registrar trazabilidad con SEA, DGA y otros organismos.'
      )}</div>
    </div>
  );
};

export default DevelopmentPermits;
